import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Plus, Edit, Trash2, Eye, EyeOff, ChevronLeft, ChevronRight,
  Settings, Upload, AlertTriangle, UserPlus, User, List, X, Phone, Mail,
  Share2, Copy, Link, RotateCcw, ExternalLink, Download, QrCode, Ticket,
  Users, CheckCircle, XCircle, Wine, MessageCircle, RefreshCw
} from "lucide-react";
import { API, BACKEND_URL } from "@/config/api";
import PasswordInput from "@/components/common/PasswordInput";

const MesasTab = ({ credentials, events, fetchEvents, isGestor, gestorPrivileges, isSuperuser, setActiveTab }) => {
  const [mesasReservations, setMesasReservations] = useState({ mesas: [], reservados: [], stats: {} });
  const [selectedEventForMesas, setSelectedEventForMesas] = useState("");
  const [loadingMesas, setLoadingMesas] = useState(false);

  const fetchMesasReservations = async (eventId) => {
    if (!eventId) {
      setMesasReservations({ mesas: [], reservados: [], stats: {} });
      return;
    }
    setLoadingMesas(true);
    try {
      const response = await axios.get(`${API}/admin/events/${eventId}/mesas`, {
        headers: { Authorization: `Basic ${credentials}` }
      });
      setMesasReservations(response.data);
    } catch (e) {
      console.error("Error fetching mesas:", e);
      setMesasReservations({ mesas: [], reservados: [], stats: {} });
    } finally {
      setLoadingMesas(false);
    }
  };

  const handleDeleteMesaReservation = async (reservationId) => {
    if (!window.confirm("¿Estás seguro de eliminar esta reserva?")) return;
    try {
      await axios.delete(`${API}/admin/mesas/${reservationId}`, {
        headers: { Authorization: `Basic ${credentials}` }
      });
      fetchMesasReservations(selectedEventForMesas);
    } catch (e) {
      alert(e.response?.data?.detail || "Error al eliminar la reserva");
    }
  };

  const downloadMesasCSV = async () => {
    if (!selectedEventForMesas) return;
    try {
      window.open(`${API}/admin/events/${selectedEventForMesas}/mesas/download`, '_blank');
    } catch (e) {
      console.error("Error downloading mesas CSV:", e);
    }
  };

  return (
    <>
          <>
            <div className="form-section mb-8">
              <h3 className="form-title mb-6 flex items-center gap-2">
                <Users size={20} className="text-yellow-400" />
                Gestión de Mesas y Reservados
              </h3>
              
              {/* Event Selector */}
              <div className="mb-6">
                <label className="text-[var(--text-muted)] text-sm mb-2 block">
                  Selecciona un evento:
                </label>
                <select
                  value={selectedEventForMesas}
                  onChange={(e) => {
                    setSelectedEventForMesas(e.target.value);
                    fetchMesasReservations(e.target.value);
                  }}
                  className="custom-input w-full max-w-md"
                  data-testid="select-event-mesas"
                >
                  <option value="">-- Selecciona un evento --</option>
                  {events.filter(e => e.mesas_enabled || e.reservados_enabled).map(event => (
                    <option key={event.id} value={event.id}>{event.nombre} - {event.fecha}</option>
                  ))}
                </select>
              </div>
              
              {selectedEventForMesas && (
                <>
                  {/* Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-[var(--surface)] p-4 border-l-4 border-[var(--primary)]">
                      <p className="text-[var(--text-muted)] text-xs uppercase">Mesas Reservadas</p>
                      <p className="text-white text-2xl font-bold">{mesasReservations.stats?.total_mesas || 0}</p>
                    </div>
                    <div className="bg-[var(--surface)] p-4 border-l-4 border-[var(--accent-cyan)]">
                      <p className="text-[var(--text-muted)] text-xs uppercase">Reservados</p>
                      <p className="text-white text-2xl font-bold">{mesasReservations.stats?.total_reservados || 0}</p>
                    </div>
                    <div className="bg-[var(--surface)] p-4 border-l-4 border-yellow-400">
                      <p className="text-[var(--text-muted)] text-xs uppercase">Personas en Mesas</p>
                      <p className="text-white text-2xl font-bold">{mesasReservations.stats?.total_personas_mesas || 0}</p>
                    </div>
                    <div className="bg-[var(--surface)] p-4 border-l-4 border-green-400">
                      <p className="text-[var(--text-muted)] text-xs uppercase">Personas en Reservados</p>
                      <p className="text-white text-2xl font-bold">{mesasReservations.stats?.total_personas_reservados || 0}</p>
                    </div>
                  </div>
                  
                  {/* Download Button */}
                  <button
                    onClick={downloadMesasCSV}
                    className="btn-download mb-6"
                    data-testid="download-mesas-csv"
                  >
                    <Download size={16} className="inline mr-2" />
                    Descargar CSV
                  </button>
                  
                  {loadingMesas ? (
                    <div className="text-center py-8">
                      <span className="spinner mx-auto"></span>
                    </div>
                  ) : (
                    <>
                      {/* Mesas List */}
                      {mesasReservations.mesas?.length > 0 && (
                        <div className="mb-8">
                          <h4 className="text-white font-bold uppercase text-sm mb-4 flex items-center gap-2">
                            <span className="w-3 h-3 bg-[var(--primary)] rounded-full"></span>
                            Mesas ({mesasReservations.mesas.length})
                          </h4>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-[var(--border-color)]">
                                  <th className="text-left py-2 px-3 text-[var(--text-muted)]">#</th>
                                  <th className="text-left py-2 px-3 text-[var(--text-muted)]">Nombre</th>
                                  <th className="text-left py-2 px-3 text-[var(--text-muted)]">Contacto</th>
                                  <th className="text-left py-2 px-3 text-[var(--text-muted)]">Personas</th>
                                  <th className="text-left py-2 px-3 text-[var(--text-muted)]">Pago</th>
                                  <th className="text-left py-2 px-3 text-[var(--text-muted)]">Estado</th>
                                  <th className="text-left py-2 px-3 text-[var(--text-muted)]">Acciones</th>
                                </tr>
                              </thead>
                              <tbody>
                                {mesasReservations.mesas.map((mesa) => (
                                  <tr key={mesa.id} className="border-b border-[var(--border-color)]/30 hover:bg-[var(--surface)]">
                                    <td className="py-2 px-3 text-[var(--primary)] font-bold">Mesa {mesa.numero_mesa}</td>
                                    <td className="py-2 px-3 text-white">{mesa.nombre}</td>
                                    <td className="py-2 px-3">
                                      <p className="text-white text-xs">{mesa.email}</p>
                                      <p className="text-[var(--text-muted)] text-xs">{mesa.telefono}</p>
                                    </td>
                                    <td className="py-2 px-3 text-white">{mesa.num_personas}</td>
                                    <td className="py-2 px-3">
                                      <span className={`text-xs px-2 py-1 ${mesa.metodo_pago === 'online' ? 'bg-[var(--accent-cyan)]/20 text-[var(--accent-cyan)]' : 'bg-gray-700 text-gray-300'}`}>
                                        {mesa.metodo_pago === 'online' ? 'Online' : 'En local'}
                                      </span>
                                    </td>
                                    <td className="py-2 px-3">
                                      {mesa.pagado || mesa.metodo_pago === 'local' ? (
                                        <span className="text-green-400 text-xs">✓ Confirmado</span>
                                      ) : (
                                        <span className="text-yellow-400 text-xs">Pendiente</span>
                                      )}
                                    </td>
                                    <td className="py-2 px-3">
                                      <button
                                        onClick={() => handleDeleteMesaReservation(mesa.id)}
                                        className="text-red-400 hover:text-red-300"
                                        title="Eliminar"
                                      >
                                        <Trash2 size={16} />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                      
                      {/* Reservados List */}
                      {mesasReservations.reservados?.length > 0 && (
                        <div>
                          <h4 className="text-white font-bold uppercase text-sm mb-4 flex items-center gap-2">
                            <span className="w-3 h-3 bg-[var(--accent-cyan)] rounded-full"></span>
                            Reservados ({mesasReservations.reservados.length})
                          </h4>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-[var(--border-color)]">
                                  <th className="text-left py-2 px-3 text-[var(--text-muted)]">#</th>
                                  <th className="text-left py-2 px-3 text-[var(--text-muted)]">Nombre</th>
                                  <th className="text-left py-2 px-3 text-[var(--text-muted)]">Contacto</th>
                                  <th className="text-left py-2 px-3 text-[var(--text-muted)]">Personas</th>
                                  <th className="text-left py-2 px-3 text-[var(--text-muted)]">Pago</th>
                                  <th className="text-left py-2 px-3 text-[var(--text-muted)]">Estado</th>
                                  <th className="text-left py-2 px-3 text-[var(--text-muted)]">Acciones</th>
                                </tr>
                              </thead>
                              <tbody>
                                {mesasReservations.reservados.map((reservado) => (
                                  <tr key={reservado.id} className="border-b border-[var(--border-color)]/30 hover:bg-[var(--surface)]">
                                    <td className="py-2 px-3 text-[var(--accent-cyan)] font-bold">Reservado {reservado.numero_mesa}</td>
                                    <td className="py-2 px-3 text-white">{reservado.nombre}</td>
                                    <td className="py-2 px-3">
                                      <p className="text-white text-xs">{reservado.email}</p>
                                      <p className="text-[var(--text-muted)] text-xs">{reservado.telefono}</p>
                                    </td>
                                    <td className="py-2 px-3 text-white">{reservado.num_personas}</td>
                                    <td className="py-2 px-3">
                                      <span className={`text-xs px-2 py-1 ${reservado.metodo_pago === 'online' ? 'bg-[var(--accent-cyan)]/20 text-[var(--accent-cyan)]' : 'bg-gray-700 text-gray-300'}`}>
                                        {reservado.metodo_pago === 'online' ? 'Online' : 'En local'}
                                      </span>
                                    </td>
                                    <td className="py-2 px-3">
                                      {reservado.pagado || reservado.metodo_pago === 'local' ? (
                                        <span className="text-green-400 text-xs">✓ Confirmado</span>
                                      ) : (
                                        <span className="text-yellow-400 text-xs">Pendiente</span>
                                      )}
                                    </td>
                                    <td className="py-2 px-3">
                                      <button
                                        onClick={() => handleDeleteMesaReservation(reservado.id)}
                                        className="text-red-400 hover:text-red-300"
                                        title="Eliminar"
                                      >
                                        <Trash2 size={16} />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                      
                      {mesasReservations.mesas?.length === 0 && mesasReservations.reservados?.length === 0 && (
                        <p className="text-[var(--text-muted)] text-center py-8">
                          No hay reservas de mesas o reservados para este evento.
                        </p>
                      )}
                    </>
                  )}
                </>
              )}
              
              {!selectedEventForMesas && (
                <p className="text-[var(--text-muted)] text-center py-8">
                  Selecciona un evento para ver las reservas de mesas y reservados.
                </p>
              )}
            </div>
          </>

    </>
  );
};

export default MesasTab;
